import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { Breadcrumbs, Tabs, Icon, Badge, Rating, Button, Skeleton, EmptyState } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { fetchHotelById } from '../../services/hotelService';

const FALLBACK_IMG = '/images/design/hero-ram-mandir.webp';

const AMENITY_ICON = [
  [/wi-?fi/i, 'wifi'], [/parking|valet/i, 'car'], [/restaurant|dining|food|breakfast|bar/i, 'utensils'],
  [/pool|swim/i, 'waves'], [/spa|ayurved|yoga|wellness/i, 'sparkles'], [/temple|darshan|tour/i, 'temple'],
  [/room service|concierge|butler|service/i, 'headset'], [/transfer|airport|shuttle/i, 'navigation'],
  [/tv|television/i, 'tv'], [/conference|meeting/i, 'users'], [/kitchen|kitchenette/i, 'utensils'],
  [/safe|security/i, 'shield-check'], [/ac\b|air.?condition/i, 'sparkles'],
];
const amenityIcon = (a = '') => (AMENITY_ICON.find(([re]) => re.test(a)) || [null, 'circle-check'])[1];
const initials = (n = '') => n.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const SAMPLE_HOTEL = {
  id: 'sample',
  name: 'Ram Palace Hotel',
  description: 'A premium spiritual retreat nestled in the heart of Ayodhya, Ram Palace Hotel offers a serene sanctuary for pilgrims and travellers. Enjoy panoramic views of the sacred Saryu river, traditional Awadhi hospitality, and modern amenities — all steps away from Ram Janmabhoomi.',
  address: 'Civil Lines, Near Ram Janmabhoomi, Ayodhya, Uttar Pradesh 224123',
  phoneNumber: '+91 98765 43210',
  email: 'reservations@rampalacehotel.in',
  rating: 4.7,
  starRating: 4,
  category: 'premium',
  priceRange: '₹3,500 - ₹9,000',
  images: [FALLBACK_IMG],
  amenities: ['Free WiFi', 'Parking', 'Restaurant', 'AC', 'Spa'],
  distanceFromLandmarks: { 'Ram Janmabhoomi': 0.4, 'Saryu Ghat': 1.2 },
  roomTypes: [
    { id: 'r1', name: 'Deluxe Room', description: 'Garden view with a king bed and modern amenities.', basePrice: 3500, maxAdults: 2, maxChildren: 1, bedType: '1 King' },
    { id: 'r2', name: 'Super Deluxe Room', description: 'Spacious room with river view and premium furnishings.', basePrice: 5500, maxAdults: 2, maxChildren: 2, bedType: '1 King or 2 Queen' },
    { id: 'r3', name: 'Suite', description: 'Separate living area with a stunning Saryu view.', basePrice: 9000, maxAdults: 3, maxChildren: 2, bedType: '1 King' },
  ],
  reviews: [
    { user: 'Ramesh Gupta', rating: 5, comment: 'Wonderful stay, very close to the temple. Staff was extremely helpful and the food was excellent.' },
    { user: 'Priya Sharma', rating: 4, comment: 'Clean rooms, great location. The spiritual ambience of the hotel is truly special.' },
    { user: 'Amit Verma', rating: 5, comment: 'Best hotel in Ayodhya. The view of Saryu from the rooftop is breathtaking.' },
  ],
};

export default function HotelDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rooms');
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    window.scrollTo(0, 0);
    setLoading(true);
    fetchHotelById(id)
      .then((data) => { if (alive) { setHotel(data); setImgIndex(0); } })
      .catch(() => { if (alive) setHotel(SAMPLE_HOTEL); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="container detail-wrap">
        <div className="detail">
          <div className="detail__main"><Skeleton height={380} radius="var(--r-xl)" /><Skeleton height={40} width="60%" /><Skeleton height={160} /></div>
          <div className="detail__aside"><Skeleton height={280} radius="var(--r-lg)" /></div>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="container detail-wrap">
        <EmptyState icon="bed-double" title={t('Stay not found')} body={t('This property is no longer listed.')} action={<Button variant="outline" to="/hotels">{t('Back to stays')}</Button>} />
      </div>
    );
  }

  const images = (hotel.images?.length ? hotel.images : [FALLBACK_IMG]);
  const km = hotel.distanceFromLandmarks?.['Ram Janmabhoomi'] ?? hotel.distanceFromLandmarks?.['Ram Mandir'];
  const roomTypes = (hotel.roomTypes || []).map((r) => ({
    id: r.id, name: r.name, description: r.description, price: Number(r.basePrice) || 0,
    capacity: `${r.maxAdults || 2} ${t('adults')}${r.maxChildren ? `, ${r.maxChildren} ${t('children')}` : ''}`,
    bedType: r.bedType, size: r.roomSize,
  }));
  const amenities = hotel.amenities || [];
  const reviews = hotel.reviews || [];
  const stars = Math.round(Number(hotel.starRating || hotel.rating) || 0);
  const fromPrice = roomTypes.length ? Math.min(...roomTypes.map((r) => r.price)) : null;

  const handleBook = () => {
    if (isAuthenticated) navigate(`/hotels/${id}/booking`);
    else navigate('/login', { state: { from: `/hotels/${id}/booking` } });
  };

  const tabs = [
    { id: 'rooms', label: t('Rooms'), icon: 'bed-double' },
    { id: 'amenities', label: t('Amenities'), icon: 'sparkles' },
    { id: 'policies', label: t('Policies'), icon: 'info' },
    { id: 'reviews', label: t('Reviews'), icon: 'star' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: hotel.name,
    description: hotel.description,
    image: images[0],
    url: `https://ayodhyadham.info/hotels/${id}`,
    priceRange: hotel.priceRange,
    telephone: hotel.phoneNumber,
    address: { '@type': 'PostalAddress', streetAddress: hotel.address, addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
    ...(hotel.rating ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: hotel.rating, reviewCount: reviews.length || 1 } } : {}),
  };

  return (
    <>
      <SEO title={`${hotel.name} — Stay in Ayodhya`} description={hotel.description} canonicalUrl={`/hotels/${id}`} image={images[0]} structuredData={schema} />

      <div className="container detail-wrap">
        <Breadcrumbs className="detail__crumbs" items={[{ label: t('Home'), to: '/' }, { label: t('Stay & Accommodation'), to: '/hotels' }, { label: hotel.name }]} />

        <div className="detail">
          <div className="detail__main">
            <div className="gallery">
              <div className="gallery__main">
                <img src={images[imgIndex]} alt={hotel.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
              </div>
              {images.length > 1 && (
                <div className="gallery__thumbs">
                  {images.map((src, i) => (
                    <button key={src} type="button" className={`gallery__thumb${i === imgIndex ? ' is-active' : ''}`} onClick={() => setImgIndex(i)} aria-label={t('View image {{n}}', { n: i + 1 })}>
                      <img src={src} alt="" loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="detail__head">
              <div className="detail__tags">
                {hotel.category && <Badge variant="soft" className="hotel-room__badge">{t(String(hotel.category).charAt(0).toUpperCase() + String(hotel.category).slice(1))}</Badge>}
                {stars > 0 && <Badge variant="gold">{t('{{n}} star', { n: stars })}</Badge>}
              </div>
              <h1 className="detail__title">{hotel.name}</h1>
              <div className="detail__meta">
                {hotel.rating != null && <span><Rating value={hotel.rating} count={reviews.length || undefined} /></span>}
                {km != null && <span><Icon name="temple" size={15} />{t('{{km}} km from Ram Mandir', { km })}</span>}
                {hotel.address && <span><Icon name="map-pin" size={15} />{hotel.address}</span>}
              </div>
            </div>

            {hotel.description && (
              <section className="detail__section">
                <h2 className="h-section">{t('About this stay')}</h2>
                <div className="prose"><p>{hotel.description}</p></div>
              </section>
            )}

            <section className="detail__section">
              <Tabs variant="underline" items={tabs} value={tab} onChange={setTab} ariaLabel={t('Hotel information')} />

              {tab === 'rooms' && (
                <div className="options" style={{ marginTop: 18 }}>
                  {roomTypes.length === 0 ? (
                    <EmptyState icon="bed-double" title={t('No room types listed')} body={t('Contact the property for availability.')} />
                  ) : roomTypes.map((r) => (
                    <div key={r.id} className="option">
                      <div>
                        <div className="option__title">{r.name}</div>
                        {r.description && <div className="option__desc">{r.description}</div>}
                        <div className="option__meta">
                          <span><Icon name="users" size={14} />{r.capacity}</span>
                          {r.bedType && <span><Icon name="bed-double" size={14} />{r.bedType}</span>}
                          {r.size && <span><Icon name="layout-grid" size={14} />{r.size} sq ft</span>}
                        </div>
                      </div>
                      <div className="option__price">
                        <strong>₹{r.price.toLocaleString('en-IN')}</strong>
                        <small>{t('/ night')}</small>
                        <div style={{ marginTop: 8 }}><Button size="sm" onClick={handleBook}>{t('Select room')}</Button></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'amenities' && (
                <div className="amenities" style={{ marginTop: 18 }}>
                  {amenities.length === 0
                    ? <p className="muted">{t('No amenities listed.')}</p>
                    : amenities.map((a) => (
                      <span key={a} className="badge badge--soft"><Icon name={amenityIcon(a)} size={14} />{a}</span>
                    ))}
                </div>
              )}

              {tab === 'policies' && (
                <ul className="info-list hotel-policies" style={{ marginTop: 18 }}>
                  <li><span className="info-list__icon"><Icon name="clock" size={16} /></span><div><div className="info-list__label">{t('Check-in / Check-out')}</div><div className="info-list__value">{t('2:00 PM / 11:00 AM')}</div></div></li>
                  <li><span className="info-list__icon"><Icon name="badge-check" size={16} /></span><div><div className="info-list__label">{t('ID proof')}</div><div className="info-list__value">{t('Valid government photo ID required for all guests')}</div></div></li>
                  <li><span className="info-list__icon"><Icon name="users" size={16} /></span><div><div className="info-list__label">{t('Children')}</div><div className="info-list__value">{t('Children below 5 stay free with existing bedding')}</div></div></li>
                  <li><span className="info-list__icon"><Icon name="indian-rupee" size={16} /></span><div><div className="info-list__label">{t('Cancellation')}</div><div className="info-list__value">{t('Free cancellation up to 24 hours before check-in')}</div></div></li>
                </ul>
              )}

              {tab === 'reviews' && (
                <div className="reviews" style={{ marginTop: 18 }}>
                  {reviews.length === 0 ? (
                    <p className="muted">{t('No reviews yet.')}</p>
                  ) : reviews.map((r, i) => (
                    <div key={`${r.user}-${i}`} className="card review">
                      <div className="review__head">
                        <span className="review__avatar">{initials(r.user)}</span>
                        <div>
                          <div className="review__name">{r.user}</div>
                          <Rating value={r.rating} />
                        </div>
                      </div>
                      <p className="review__text">{r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="detail__aside">
            <div className="card aside-card">
              <span className="eyebrow">{t('Book your stay')}</span>
              <div className="aside-card__price">
                {fromPrice ? <><small>{t('from')}</small><strong>₹{fromPrice.toLocaleString('en-IN')}</strong><small>{t('/ night')}</small></> : <strong>{hotel.priceRange || t('On request')}</strong>}
              </div>
              <div className="aside-card__rows">
                <div className="aside-card__row"><span>{t('Check-in')}</span><span>{t('2:00 PM')}</span></div>
                <div className="aside-card__row"><span>{t('Check-out')}</span><span>{t('11:00 AM')}</span></div>
                {km != null && <div className="aside-card__row"><span>{t('Ram Mandir')}</span><span>{t('{{km}} km', { km })}</span></div>}
                {hotel.phoneNumber && <div className="aside-card__row"><span>{t('Phone')}</span><span><a href={`tel:${hotel.phoneNumber.replace(/[^+\d]/g, '')}`}>{hotel.phoneNumber}</a></span></div>}
                {hotel.email && <div className="aside-card__row"><span>{t('Email')}</span><span><a href={`mailto:${hotel.email}`}>{hotel.email}</a></span></div>}
              </div>
              <Button block onClick={handleBook} icon={<Icon name="arrow-right" size={16} />}>{t('Book now')}</Button>
              {!isAuthenticated && <p className="aside-card__note">{t("You'll need to sign in to complete your booking.")}</p>}
            </div>

            <div className="card aside-card" style={{ background: 'var(--surface-2)' }}>
              <h3 className="h-card">{t('Need help?')}</h3>
              <p className="aside-card__note">{t('Our team can help you pick the right stay for your darshan slot.')}</p>
              <Button size="sm" variant="outline" to="/contact" icon={<Icon name="arrow-right" size={14} />}>{t('Contact Us')}</Button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
